import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Menu, Search, Bell, LogOut, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { etiquetaAmbienteDev } from './AmbienteBanner';
import '../styles/Navbar.css';
import '../styles/AmbienteBanner.css';

// Se evalúan en orden: la primera coincidencia gana, así que las rutas
// específicas van antes que las generales.
const PAGE_META = [
  [/^\/dashboard\/?$/, { title: 'Dashboard', subtitle: 'Resumen de la operación' }],
  [/^\/$/, { title: 'Dashboard', subtitle: 'Resumen de la operación' }],
  [/^\/clientes\/nuevo\/?$/, { title: 'Nuevo cliente', subtitle: 'Alta de cliente en seis pasos' }],
  [/^\/clientes\/[^/]+\/editar\/?$/, { title: 'Editar cliente', subtitle: 'Modificar la información del expediente' }],
  [/^\/clientes\/[^/]+\/?$/, { title: 'Expediente', subtitle: 'Información y actividad del cliente' }],
  [/^\/clientes\/?$/, { title: 'Clientes', subtitle: 'Alta, edición y consulta de clientes' }],
  [/^\/creditos\/nuevo\/?$/, { title: 'Nuevo crédito', subtitle: 'Registro y configuración del crédito' }],
  [/^\/creditos\/[^/]+\/?$/, { title: 'Expediente de crédito', subtitle: 'Detalle y seguimiento del crédito' }],
  [/^\/creditos\/?$/, { title: 'Créditos', subtitle: 'Solicitudes y seguimiento de crédito' }],
  [/^\/(agrupaciones|cartera)\/[^/]+\/?$/, { title: 'Cartera de Créditos', subtitle: 'Detalle de la agrupación' }],
  [/^\/(agrupaciones|cartera)\/?$/, { title: 'Cartera de Créditos', subtitle: 'Créditos individuales, grupos y colocadoras' }],
  [/^\/cobranza\/?$/, { title: 'Cobranza', subtitle: 'Cartera y seguimiento' }],
  [/^\/pagos\/?$/, { title: 'Pagos', subtitle: 'Bandeja global de pagos' }],
  [/^\/usuarios\/?$/, { title: 'Usuarios', subtitle: 'Administración de cuentas y roles' }],
  [/^\/perfil\/?$/, { title: 'Mi Perfil', subtitle: 'Información y configuración de cuenta' }],
  [/^\/catalogos\/?$/, { title: 'Catálogos', subtitle: 'Categorías y productos del sistema' }]
];

const DEFECTO = { title: 'Sistema MIDAS', subtitle: 'Operaciones financieras' };

function Navbar({ onMenuClick }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout, reviewMode, ambiente, systemInfo } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const meta = PAGE_META.find(([patron]) => patron.test(pathname))?.[1] || DEFECTO;

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const iniciales = (user?.nombre ? user.nombre.charAt(0) : 'U').toUpperCase();
  const rolNombre = user?.rol?.nombre || user?.rol?.codigo || 'Usuario';
  const esAdmin = user?.rol?.codigo === 'ADMIN' || user?.rol?.codigo === 'SUPERADMIN' || rolNombre.toUpperCase().includes('ADMIN');

  return (
    <header className="navbar">
      <div className="navbar-left">
        <button className="navbar-menu-btn" onClick={onMenuClick} aria-label="Abrir menú">
          <Menu size={20} strokeWidth={1.75} />
        </button>

        <div className="navbar-heading">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 className="navbar-title">{meta.title}</h1>
            {(esAdmin || ambiente === 'DEV') && (
              <span className={`supabase-review-badge ${ambiente === 'DEV' ? 'is-dev' : (reviewMode ? 'is-review' : 'is-production')}`}>
                <span className={`supabase-review-dot ${ambiente === 'DEV' ? 'is-dev' : (reviewMode ? 'is-review' : 'is-production')}`} />
                {ambiente === 'DEV'
                  ? etiquetaAmbienteDev(systemInfo?.environment)
                  : (reviewMode ? 'SUPABASE · MODO REVISIÓN' : 'SUPABASE · PRODUCCIÓN')}
              </span>
            )}
          </div>
          <span className="navbar-rule" aria-hidden="true" />
          <p className="navbar-subtitle">{meta.subtitle}</p>
        </div>
      </div>

      <div className="navbar-right">
        <div className="navbar-search">
          <Search size={16} strokeWidth={1.75} className="navbar-search-icon" />
          <input type="search" placeholder="Buscar" aria-label="Buscar" />
        </div>

        <button className="btn btn-icon" aria-label="Notificaciones">
          <Bell size={18} strokeWidth={1.75} />
        </button>

        <div className="navbar-profile-wrapper" ref={dropdownRef}>
          <div
            id="navbar-profile-btn"
            className="navbar-profile"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            role="button"
            tabIndex={0}
          >
            <span className="navbar-avatar">
              {iniciales}
            </span>
            <div className="navbar-profile-info">
              <span className="navbar-profile-name">{user?.nombre || 'Usuario'}</span>
              <span className="navbar-profile-role">{rolNombre}</span>
            </div>
          </div>

          {dropdownOpen && (
            <div className="navbar-profile-dropdown">
              <Link
                to="/perfil"
                className="navbar-dropdown-item"
                onClick={() => setDropdownOpen(false)}
              >
                <User size={16} />
                <span>Mi Perfil</span>
              </Link>
              <div className="navbar-dropdown-divider" />
              <button
                type="button"
                className="navbar-dropdown-item navbar-dropdown-item-danger"
                onClick={handleLogout}
              >
                <LogOut size={16} />
                <span>Cerrar Sesión</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Navbar;
